import { Fragment, useEffect, useRef, useState, useCallback } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"

// ** UI Components
import { Card } from "@components/common/card"
import PmsLoader from "@components/common/loader"
import { Switch } from "@components/common/switch"

// ** Types
import {
  BreadcrumbsMap,
  DictType,
  FilesViewProps,
  FolderItemsMap,
  FolderMetaMap,
  ProjectFile,
  SortingObject,
} from "@pma/types"

// ** Routes
import {
  FOLDER_ENDPOINT,
  SERVER_URL,
  MOVE_FILE_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  ADD_FILES_TO_PROJECT_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Constants
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"

// ** Helpers
import {
  cn,
  collapseSubfolders,
  debounce,
  fetchAndCreateFile,
  downloadFile as downloadFileUtil,
} from "@pma/utils/helpers"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"
import { useAuth } from "@pma/stores/auth-store"
import { FilesViewContext } from "@pma/hooks/use-files-view"

// Sub-components
import MoveFile from "../move-file"
import FilesToolbar from "./components/files-toolbar"
import FilesBatchActions from "../files-batch-actions"
import ActiveFilters from "./components/active-filters"
import BreadcrumbsNav from "./components/breadcrumbs-nav"
import VirtualGridView from "./components/virtual-grid-view"
import VirtualListView from "./components/virtual-list-view"
import FolderTreeSidebar from "./components/folder-tree-sidebar"
import ShareFilesFoldersViaEmail from "../share-files-folders-via-email"

export default function FilesView({
  project,
  closeModal,
  per_page = null,
  showAsModal = false,
  refetchAllFolder = () => {},
}: FilesViewProps) {
  // --- Auth ---
  const { authConfig } = useAuth()
  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find(
    (company: any) => company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */,
  )

  // --- Root folder ---
  const rootFolder = {
    ...project?.root_folder,
    deleted: project.deleted,
    deleted_at: project.deleted_at,
    deleted_by: project.deleted_by,
  }

  // --- Translation ---
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  // --- Navigation ---
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const queryKey = searchParam.get("revalidate")
  const queryAction = searchParam.get("action")
  const accessToken = parseCookies()[ACCESS_TOKEN]

  // --- Core state ---
  const [activeFolder, setActiveFolder] = useState<DictType>(rootFolder ?? {})
  const [folderItems, setFolderItems] = useState<FolderItemsMap>({})
  const [foldersMeta, setFoldersMeta] = useState<FolderMetaMap>({})
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbsMap>({
    [rootFolder?.id]: [rootFolder],
  })

  // --- UI state ---
  const [search, setSearch] = useState("")
  const [isShow, setIsShow] = useState(false)
  const [showBatchSelection, setShowBatchSelection] = useState(false)
  const [batchData, setBatchData] = useState<DictType[]>([])
  const [view, setView] = useState(0) // 0 = grid, 1 = list
  const [showDeleted, setShowDeleted] = useState(false)
  const [sortingObj, setSortingObj] = useState<SortingObject>({ sorting: "name", order: "asc" })
  const [fileTypes, setFileTypes] = useState<string[]>([])
  const [filterActive, setFilterActive] = useState(false)
  const [clearFilter, setClearFilter] = useState(false)
  const [newActiveData, setNewActiveData] = useState<any>({})

  // --- File operations state ---
  const [folderId, setFolderId] = useState(0)
  const [isMenuView, setIsMenuView] = useState(false)
  const [cutFiles, setCutFiles] = useState<DictType[]>([])
  const [copyFiles, setCopyFiles] = useState<DictType[]>([])
  const [moveFileModal, setMoveFileModal] = useState(false)
  const [selectedMoveFile, setSelectedMoveFile] = useState<any>({})
  const [recentDeletedFolder, setRecentDeletedFolder] = useState(0)
  const [selectedFiles, setSelectedFiles] = useState<DictType[]>([])
  const [shareViaEmailModal, setShareViaEmailModal] = useState(false)
  const [recentDeletedFile, setRecentDeletedFile] = useState<any[]>([])
  const [preFolderItems, setPreFolderItems] = useState<FolderItemsMap>({})

  // --- Pagination ---
  const [pageParam, setPageParam] = useState<number | null>(1)
  const [nextPage, setNextPage] = useState<number | null>(0)

  // --- Refs ---
  const isSubFolderRef = useRef(false)

  // --- Constants ---
  const imageExtensions = ["png", "webp", "jpeg", "jpg"]
  const printAble = ["jpg", "jpeg", "png", "pdf"]

  // ================================================================
  // API: Fetch folder content
  // ================================================================
  const getFolderContent = async (folderId: number) => {
    const payload: any = { folder_id: folderId }

    if (search) {
      payload.file_name_filter = search
      payload.subfolder_name_filter = search
    }
    if (sortingObj.sorting !== "" && sortingObj.order) {
      payload.file_sorting = { orderBy: sortingObj.order, sortBy: sortingObj.sorting }
      payload.folder_sorting = {
        orderBy: sortingObj.order,
        sortBy: sortingObj.sorting === "size" ? "folder_size" : sortingObj.sorting,
      }
    }
    if (showDeleted) payload.show_deleted = showDeleted
    if (fileTypes.length) payload.file_type_filter = fileTypes

    const response = await axios.post(`${SERVER_URL}${FOLDER_ENDPOINT}`, payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    isSubFolderRef.current = false
    return response.data
  }

  const {
    data: folderContent,
    isLoading,
    isFetching,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: [activeFolder, "get-curr-folder-files", showDeleted],
    queryFn: () => getFolderContent(activeFolder?.id),
    enabled: !!activeFolder?.id,
  })

  // ================================================================
  // API: Move file
  // ================================================================
  const moveFile = async (params: { files_id: number[]; folder_id: number }) => {
    const response = await axios.post(SERVER_URL + MOVE_FILE_ENDPOINT, params, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const refetchFolderContent = (folderId: number) => {
    queryClient.invalidateQueries({ queryKey: [{ id: folderId }, null, "get-curr-folder-files"] })
  }

  const moveFileMutation = useMutation({
    mutationKey: ["move-file"],
    mutationFn: moveFile,
    onSuccess: (data, variables) => {
      const fileIds = variables.files_id
      const sourceFolderIds = new Set<number>()
      let singleFileName: string | undefined
      if (fileIds.length === 1) {
        singleFileName = cutFiles.find((f) => f.id === fileIds[0])?.name
      }

      fileIds.forEach((fileId: number) => {
        const sourceFolder = foldersMeta[fileId]
        if (sourceFolder) sourceFolderIds.add(sourceFolder)
      })

      sourceFolderIds.forEach((sf) => {
        setFolderItems((prev) => ({
          ...prev,
          [sf]: prev[sf].filter((item) => !fileIds.includes(item.id)),
        }))
        refetchFolderContent(sf)
      })

      const targetFolderId = variables.folder_id
      if (targetFolderId) refetchFolderContent(targetFolderId)

      const targetFolder = Object.values(folderItems)
        .flat()
        .find((i) => i.id === targetFolderId && i.as === "folder")
      if (targetFolder) handleTreeItemFolderClick(targetFolder, true)
      if (data.id === rootFolder.id) handleTreeItemFolderClick(data, true)

      resetPageParam()
      toast({
        title: translate("Success"),
        description: fileIds.length > 1
                ? `${fileIds.length} ${translate("files")} ${translate("have been moved")}`
                : `${singleFileName ?? "File"} ${translate("has been moved")}`,
        variant: "success",
      })
      setCutFiles([])
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
    },
    onError: () => {
      const fileIds = moveFileMutation.variables?.files_id || []
      toast({
        title: translate("Error"),
        description: fileIds.length > 1
                ? `${translate("Unable to move")} ${fileIds.length} ${translate("files")}`
                : `${translate("Unable to move")} ${cutFiles.find((f) => f.id === fileIds[0])?.name ?? "File"}`,
        variant: "error",
      })
    },
  })

  // ================================================================
  // API: Upload file (for copy-paste)
  // ================================================================
  const uploadFile = async (files: File[]) => {
    const formData = new FormData()
    if (!files?.length) return
    files.forEach((file) => formData.append("files", file))
    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`
    const response = await axios.post(url, formData, {
      headers: { accept: "*/*", "Content-Type": "multipart/form-data" },
    })
    return response?.data
  }

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: (data) => {
      setCopyFiles([])
      uploadToProjectMutation.mutate(data as ProjectFile[])
    },
    onError: (error: DictType) => {
      setIsShow(false)
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to copy file at this time"),
        variant: "error",
      })
    },
  })

  const uploadToProject = async (files: ProjectFile[]) => {
    const file_ids = files?.map((f) => f.id)
    const body = activeFolder?.isRoot
      ? { project_id: project?.id }
      : { folder_id: activeFolder?.id }
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_PROJECT_ENDPOINT,
      { ...body, files_id: file_ids, inform_users: true },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const uploadToProjectMutation = useMutation({
    mutationKey: ["add-files-to-project", project?.id],
    mutationFn: uploadToProject,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Project file Copied"),
        variant: "success",
      })
      refetch()
      setIsShow(false)
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to copy file at this time"),
        variant: "error",
      })
    },
  })

  // ================================================================
  // Action handlers
  // ================================================================
  const resetPageParam = () => setPageParam(1)

  const handlePasteFiles = () => {
    if (cutFiles.length === 0 || !activeFolder?.id) return
    const folderId = cutFiles[0]?.folders_id
    const fileIds = cutFiles.map((f) => f.id)
    setFolderItems((prev) => ({
      ...prev,
      [folderId]: prev[folderId].filter((f) => !fileIds.includes(f.id)),
    }))
    moveFileMutation.mutate({ files_id: fileIds, folder_id: activeFolder.id })
  }

  const handlePateCopyFiles = async () => {
    const condition = copyFiles.some((f) => foldersMeta[f.id] === activeFolder.id)
    const fileObjects = await Promise.all(
      copyFiles.map((item) => fetchAndCreateFile(item.file, item.name, condition)),
    )
    setIsShow(true)
    uploadFileMutation.mutate(fileObjects)
  }

  const handleClearCutFunctionality = () => {
    setCutFiles([])
    setFolderItems((prev) => ({ ...prev, [`${folderId}`]: preFolderItems[`${folderId}`] }))
    setPreFolderItems({})
    setFolderId(0)
  }

  const handleClearCopyFunctionality = () => setCopyFiles([])

  const handleTreeItemFolderClick = useCallback(
    (item: DictType, forceExpand: boolean = false) => {
      const parentId: number = foldersMeta[item.id]
      const isCurrentlyExpanded = item.isExpanded
      const willExpand = forceExpand || !isCurrentlyExpanded

      if (willExpand) {
        isSubFolderRef.current = true
        setPageParam(1)
        setActiveFolder(item)
        if (activeFolder?.id === item.id && !forceExpand) refetch()
      } else {
        setActiveFolder(item)
      }

      if (parentId) {
        if (!willExpand) {
          const data = collapseSubfolders(folderItems, item.id, true)
          setFolderItems(data)
        } else {
          setFolderItems((prev) => ({
            ...prev,
            [parentId]: prev[parentId].map((_item) =>
              _item.id === item.id
                ? { ..._item, isExpanded: forceExpand || !_item.isExpanded }
                : _item,
            ),
          }))
        }
      } else if (item.id === rootFolder.id) {
        if (!willExpand) collapseSubfolders(folderItems, item.id, true)
      }

      setBatchData([])
      setShowBatchSelection(false)
    },
    [folderItems, foldersMeta, activeFolder, rootFolder],
  )

  const handleBatchSelection = () => {
    setBatchData([])
    setShowBatchSelection(!showBatchSelection)
  }

  const selectAllFiles = () => {
    setBatchData([...(folderItems[activeFolder?.id] ?? [])])
  }

  const clearSelection = () => setBatchData([])

  const handleSelection = (selected: DictType, event: React.SyntheticEvent) => {
    const { checked } = event.target as HTMLInputElement
    if (checked) {
      setBatchData([selected, ...batchData])
    } else {
      setBatchData(batchData.filter((val: any) => val.id !== selected.id))
    }
  }

  const handleLoadMoreData = () => {
    if (nextPage) setPageParam(nextPage)
  }

  const downloadFile = useCallback((file: any) => downloadFileUtil(file), [])

  // ================================================================
  // Effects
  // ================================================================

  // Process folder content data
  useEffect(() => {
    if (isFetched && folderContent) {
      const { files, subfolders } = folderContent
      const folderMetaData: FolderMetaMap = { ...foldersMeta }
      const newBreadcrumbs: BreadcrumbsMap = { ...breadcrumbs }

      const rootFolderFiles =
        files
          ?.map((item: DictType) => {
            if (!recentDeletedFile.includes(item.id)) {
              folderMetaData[item?.id] = activeFolder?.id
              return { ...item, as: "file", isExpanded: false }
            }
          })
          .filter(Boolean) || []

      const rootFolderSubFolders =
        subfolders
          ?.map((item: DictType) => {
            if (item.id !== recentDeletedFolder) {
              folderMetaData[item?.id] = activeFolder?.id
              newBreadcrumbs[item?.id] = [...(newBreadcrumbs[activeFolder?.id] || []), item]
              return { ...item, as: "folder", isExpanded: false }
            }
          })
          .filter(Boolean) || []

      setFoldersMeta(folderMetaData)
      setBreadcrumbs(newBreadcrumbs)
      setRecentDeletedFolder(0)
      setRecentDeletedFile([])

      setFolderItems((prev) => ({
        ...prev,
        [activeFolder?.id]: [...rootFolderSubFolders, ...rootFolderFiles],
      }))

      setNextPage(files.nextPage || null)
      if (clearFilter) setClearFilter(false)
      if (filterActive) setFilterActive(false)
    }
  }, [isFetched, folderContent])

  // Handle URL-based revalidation
  useEffect(() => {
    if (queryKey === "projects") {
      if (rootFolder.id === activeFolder.id) {
        refetch()
      } else {
        if (queryAction === "delete") {
          if (breadcrumbs[activeFolder.id]?.length > 1) {
            const parent = breadcrumbs[activeFolder.id][breadcrumbs[activeFolder.id].length - 2]
            setActiveFolder(parent)
            setPageParam(1)
            isSubFolderRef.current = true
            refetch()
          } else {
            closeModal?.()
          }
        } else {
          refetch()
        }
      }
      navigate(`${location.pathname}`)
    }
  }, [queryKey, queryAction])

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(() => refetch(), 300),
    [],
  )
  useEffect(() => {
    debouncedSearch()
  }, [search])

  // Refetch on filter/sort changes
  useEffect(() => {
    if (fileTypes) refetch()
  }, [fileTypes])

  useEffect(() => {
    if (sortingObj.sorting !== "" && sortingObj.order) refetch()
  }, [sortingObj])

  // Handle clear filter
  useEffect(() => {
    if (clearFilter) {
      setSearch("")
      setSortingObj({ sorting: "name", order: "asc" })
      setFileTypes([])
    } else {
      if (Object.keys(newActiveData).length > 0) handleTreeItemFolderClick(newActiveData, true)
      else setNewActiveData({})
    }
  }, [clearFilter])

  // Track filter active state
  useEffect(() => {
    setFilterActive(search !== "" || fileTypes.length !== 0)
  }, [search, fileTypes])

  // ================================================================
  // Early return
  // ================================================================
  if (!rootFolder?.id) return <PmsLoader />

  // ================================================================
  // Context value
  // ================================================================
  const contextValue = {
    // State
    activeFolder,
    setActiveFolder,
    folderItems,
    setFolderItems,
    foldersMeta,
    setFoldersMeta,
    breadcrumbs,
    setBreadcrumbs,
    rootFolder,
    search,
    setSearch,
    isShow,
    setIsShow,
    showBatchSelection,
    setShowBatchSelection,
    batchData,
    setBatchData,
    cutFiles,
    setCutFiles,
    copyFiles,
    setCopyFiles,
    moveFileModal,
    setMoveFileModal,
    isMenuView,
    setIsMenuView,
    selectedMoveFile,
    setSelectedMoveFile,
    selectedFiles,
    setSelectedFiles,
    shareViaEmailModal,
    setShareViaEmailModal,
    showDeleted,
    setShowDeleted,
    view,
    setView,
    sortingObj,
    setSortingObj,
    fileTypes,
    setFileTypes,
    filterActive,
    setFilterActive,
    clearFilter,
    setClearFilter,
    newActiveData,
    setNewActiveData,
    recentDeletedFolder,
    setRecentDeletedFolder,
    recentDeletedFile,
    setRecentDeletedFile,
    preFolderItems,
    setPreFolderItems,
    folderId,
    setFolderId,

    // Computed
    isOrganizationOwner,
    isAdmin,
    imageExtensions,
    printAble,

    // Actions
    handleTreeItemFolderClick,
    handlePasteFiles,
    handlePateCopyFiles,
    handleClearCutFunctionality,
    handleClearCopyFunctionality,
    handleBatchSelection,
    selectAllFiles,
    clearSelection,
    handleSelection,
    downloadFile,
    resetPageParam,
    refetch,
    refetchFolderContent,
    refetchAllFolder,

    // Query state
    isLoading,
    isFetching,
    isFetched,
    nextPage,
    pageParam,
    setPageParam,
    handleLoadMoreData,

    // Project
    project,
  }

  // ================================================================
  // Render
  // ================================================================
  return (
    <FilesViewContext.Provider value={contextValue}>
      <Fragment>
        {/* Show deleted toggle */}
        <div className='my-1 flex items-center justify-end'>
          <div className='flex items-center gap-2'>
            <Switch
              checked={showDeleted}
              className='h-6 w-11'
              onCheckedChange={(checked) => setShowDeleted(checked)}
            />
            <h4 className='text-sm font-semibold'>{translate("Show Deleted Files and Folders")}</h4>
          </div>
        </div>

        {/* Full-screen loader */}
        {isShow && (
          <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
            <div className='h-16 w-16 animate-spin rounded-full border-4 border-white border-t-transparent' />
          </div>
        )}

        {/* Main card layout */}
        <Card
          className={cn(
            "relative flex h-full w-full flex-row gap-2 overflow-hidden py-3",
            isHebrew ? "rtl" : "ltr",
          )}
        >
          {/* Left sidebar: Folder tree */}
          <FolderTreeSidebar />

          {/* Right content area */}
          <div className='w-full gap-4'>
            <BreadcrumbsNav />
            <FilesToolbar />
            <ActiveFilters />

            {/* Content view */}
            {view === 1 ? (
              <VirtualListView showAsModal={showAsModal} />
            ) : (
              <VirtualGridView showAsModal={showAsModal} />
            )}
          </div>
        </Card>

        {/* Move file modal */}
        {moveFileModal && (
          <MoveFile
            file={selectedMoveFile}
            moveFileModal={moveFileModal}
            resetPageParam={resetPageParam}
            setMoveFileModal={setMoveFileModal}
            rootFolder={rootFolder}
            parentFolder={activeFolder}
            setCutFiles={setCutFiles}
            isMenuView={isMenuView}
            setFolderItems={setFolderItems}
            foldersMeta={foldersMeta}
            folderItems={folderItems}
            cutFiles={cutFiles}
            setFoldersMeta={setFoldersMeta}
            handleBatchSelection={handleBatchSelection}
            handleTreeItemFolderClick={handleTreeItemFolderClick}
            fileArr={[]}
            setBreadcrumbs={setBreadcrumbs}
            breadcrumbs={breadcrumbs}
            setActiveFolder={setActiveFolder}
          />
        )}

        {/* Batch actions panel */}
        {showBatchSelection && (
          <FilesBatchActions
            tasks={batchData}
            showBatchSelection={showBatchSelection}
            handleBatchSelection={handleBatchSelection}
            rootFolder={rootFolder}
            resetPageParam={resetPageParam}
            activeFolder={activeFolder}
            selectAllFiles={selectAllFiles}
            totalFiles={folderItems[activeFolder?.id]?.length ?? 0}
            setCutFiles={setCutFiles}
            setCopyFiles={setCopyFiles}
            clearSelection={clearSelection}
            setFolderItems={setFolderItems}
            setBreadcrumbs={setBreadcrumbs}
            foldersMeta={foldersMeta}
            folderItems={folderItems}
            cutFiles={cutFiles}
            handleTreeItemFolderClick={handleTreeItemFolderClick}
            setFoldersMeta={setFoldersMeta}
            breadcrumbs={breadcrumbs}
            setActiveFolder={setActiveFolder}
            setPreFolderItems={setPreFolderItems}
            setFolderId={setFolderId}
            setShareViaEmailModal={setShareViaEmailModal}
            setSelectedFiles={setSelectedFiles}
          />
        )}

        {/* Share via email modal */}
        {shareViaEmailModal && (
          <ShareFilesFoldersViaEmail
            modalOpen={shareViaEmailModal}
            setModalOpen={setShareViaEmailModal}
            selectedFiles={selectedFiles}
            project={project}
          />
        )}
      </Fragment>
    </FilesViewContext.Provider>
  )
}
